import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { InView } from 'react-intersection-observer'
import { AddToTrolley, Row } from '@sainsburys-tech/bolt'
import HotSpot from '../../functional/hot-spot/HotSpot'
import { hotspotTracking } from '../../functional/hot-spot/hot-spot.tracking'
import { agnostifyUrl } from '../../../helpers/utils'
import { createGolDataAttributes } from '../../../helpers/tracking'
import * as Styled from './M038.styles'

/**
 * M038 is an inspirational lifestyle banner with interactive hotspots and quick view component, title, content and static images.
 */

const getProductId = (product) => product.target.substr(product.target.lastIndexOf('/') + 1)

const M038 = ({ heading, mainImage, imageFormat, link, items, onLinkClick }) => {
  const { hotspots, pointOfInterest } = mainImage
  const findMoreText = 'Find more Inspiration'
  const isEditorial = !!items.length

  if (!pointOfInterest) return null

  const allProducts = hotspots.map((hotSpot) => {
    return { id: getProductId(hotSpot), hotspotProductType: 'main' }
  })

  useEffect(() => {
    hotspotTracking('hotspotLoaded', heading, allProducts)
  }, [heading])

  const getGOLDataAttributes = (action) =>
    createGolDataAttributes('isolated', 'M038 - NA', link?.title, link?.url, heading, action)

  const addGolTracking = (event) => {
    const dataAttributes = getGOLDataAttributes()

    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event, linkUrl, linkTitle) => {
    if (onLinkClick) {
      onLinkClick(event, agnostifyUrl(linkUrl))
    }
    addGolTracking(event, linkTitle, linkUrl)
  }

  return (
    <InView onChange={(inView) => inView && hotspotTracking('hotspotViewed', heading, allProducts)} triggerOnce>
      <Styled.Wrapper data-test='m038-container' {...getGOLDataAttributes('Load')}>
        <AddToTrolley.Context>
          {heading && (
            <Row gutterValue={0} row='xs' data-test='m038-heading-container'>
              <Styled.HeadingWrapper>
                <Styled.Heading data-test='m038-heading'>{heading}</Styled.Heading>
                {link && (
                  <Styled.MoreInspirationLink>
                    <a
                      href={link?.url}
                      target={link?.target}
                      title={link?.title}
                      data-test='m038-more-link'
                      onClick={(evt) => ctaClick(evt, link?.url, link?.title)}
                    >
                      {findMoreText}
                    </a>
                  </Styled.MoreInspirationLink>
                )}
              </Styled.HeadingWrapper>
            </Row>
          )}
          <AddToTrolley.Modal />
          <HotSpot
            data-test='m038-hotspot'
            image={mainImage.source}
            imageFormat={imageFormat}
            altText={mainImage.altText}
            hotSpots={hotspots}
            pointOfInterest={pointOfInterest}
            heading={heading}
            isEditorial={isEditorial}
            items={items}
          />
          {link && (
            <Styled.MoreLinkWrapper>
              <Styled.MoreInspirationLinkAlt>
                <a href={link?.url} target={link?.target} title={link?.title} data-test='m038-more-link'>
                  {findMoreText}
                </a>
              </Styled.MoreInspirationLinkAlt>
            </Styled.MoreLinkWrapper>
          )}
        </AddToTrolley.Context>
      </Styled.Wrapper>
    </InView>
  )
}

M038.propTypes = {
  /** Required text for heading */
  heading: PropTypes.string.isRequired,
  /** Main image for use with hotspots */
  mainImage: PropTypes.shape({
    /** Image URL */
    source: PropTypes.string.isRequired,
    /** Alt text for the image */
    altText: PropTypes.string.isRequired,
    /** Optional hotspot coordinates set by the authors in Amplience */
    pointOfInterest: PropTypes.shape({
      w: PropTypes.number,
      h: PropTypes.number,
      x: PropTypes.number,
      y: PropTypes.number
    }),
    /** Array of hotspots */
    hotspots: PropTypes.array
  }).isRequired,
  /** Optional image format, eg. avif */
  imageFormat: PropTypes.string,
  /** Link details for find more inspiration */
  link: PropTypes.shape({
    /** Link URL */
    url: PropTypes.string.isRequired,
    /** Link title, can be seen when hovering over the CTA */
    title: PropTypes.string.isRequired,
    /** Option of opening in a new tab, defaults to _blank  */
    target: PropTypes.oneOf(['_blank', '_self'])
  }),
  /** Array of items to be displayed */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /** Required text for heading */
      heading: PropTypes.string.isRequired,
      /** Required text for heading */
      bodyCopy: PropTypes.string.isRequired,
      link: PropTypes.shape({
        /** Link label */
        copy: PropTypes.string,
        /** Optional link title, can be seen when hovering over the image */
        title: PropTypes.string,
        /** Link URL */
        url: PropTypes.string,
        /** Link style */
        style: PropTypes.oneOf(['link', 'primary', 'secondary']),
        /** Option of opening in a new tab, defaults to an empty string  */
        linkTarget: PropTypes.oneOf(['', '_blank', '_self'])
      }),
      image: PropTypes.shape({
        /** Image URL */
        url: PropTypes.string.isRequired,
        /** Alt text for the image */
        altText: PropTypes.string.isRequired
      })
    })
  )
}

M038.defaultProps = {
  hotspots: [],
  items: []
}

export default M038
